/**
 * vociTraduzione — l'elenco dei testi di un questionario da tradurre, con il testo sorgente
 * accanto, e la lettura/scrittura di una voce dentro `TraduzioneQuestionario`. PURA.
 *
 * La regola di che cosa si traduce e di che cosa manca sta in `shared/questionari/traduzioni.ts`
 * (`questionarioInLingua(...).mancanti`): il conteggio delle mancanti lo fa SEMPRE quella. Qui si
 * cammina la stessa forma per poter MODIFICARE le voci, e il percorso di ogni voce si scrive con
 * gli stessi segmenti uniti da un punto: così una voce si riconosce fra le mancanti per uguaglianza.
 *
 * I segmenti sono un ELENCO, non una stringa da spezzare: i codici storici delle opzioni (il testo
 * stesso) possono contenere punti.
 */

import type { Questionario, TraduzioneQuestionario } from '@shared/questionari';

export interface VoceTraduzione {
  /** I segmenti dentro `TraduzioneQuestionario` (es. ['domande', 'settori', 'opzioni', 'vepa']). */
  segmenti: string[];
  /** Gli stessi uniti da un punto: è la forma di `mancanti`. */
  percorso: string;
  /** Il gruppo in cui si mostra («Domanda 2 · Cosa vende»). */
  gruppo: string;
  /** Che cosa è («Testo», «Opzione «Magazzino»»). */
  cosa: string;
  sorgente: string;
  /** Un indirizzo: si può cambiare per lingua, ma non conta come traduzione mancante. */
  riferimento: boolean;
  /** Testo lungo: campo su più righe. */
  lungo: boolean;
}

function pieno(v: unknown): v is string {
  return typeof v === 'string' && v.trim() !== '';
}

export function vociDelQuestionario(q: Questionario): VoceTraduzione[] {
  const voci: VoceTraduzione[] = [];
  const aggiungi = (segmenti: string[], gruppo: string, cosa: string, sorgente: unknown, extra?: { riferimento?: boolean; lungo?: boolean }) => {
    if (!pieno(sorgente)) return;
    voci.push({
      segmenti,
      percorso: segmenti.join('.'),
      gruppo,
      cosa,
      sorgente,
      riferimento: extra?.riferimento === true,
      lungo: extra?.lungo === true || sorgente.length > 90,
    });
  };

  aggiungi(['titolo'], 'Questionario', 'Titolo', q.titolo);
  aggiungi(['descrizione'], 'Questionario', 'Descrizione', q.descrizione, { lungo: true });

  if (q.apertura) {
    const g = 'Apertura';
    aggiungi(['apertura', 'titolo'], g, 'Titolo', q.apertura.titolo);
    aggiungi(['apertura', 'testo'], g, 'Testo', q.apertura.testo, { lungo: true });
    aggiungi(['apertura', 'etichetta'], g, 'Etichetta piccola', q.apertura.etichetta);
    aggiungi(['apertura', 'bottone'], g, 'Bottone', q.apertura.bottone);
  }

  q.domande.forEach((d, i) => {
    const g = `Domanda ${i + 1} · ${d.nome_breve || d.testo}`;
    const base = ['domande', d.id];
    aggiungi([...base, 'testo'], g, 'Testo', d.testo);
    aggiungi([...base, 'nome_breve'], g, 'Nome breve', d.nome_breve);
    aggiungi([...base, 'aiuto'], g, 'Riga di aiuto', d.aiuto);
    aggiungi([...base, 'segnaposto'], g, 'Esempio nel campo', d.segnaposto);
    (d.opzioni ?? []).forEach((o) => aggiungi([...base, 'opzioni', o.id], g, `Opzione «${o.etichetta}»`, o.etichetta));
    if (d.campo_dettaglio) {
      aggiungi([...base, 'campo_dettaglio', 'etichetta'], g, 'Campo che compare · etichetta', d.campo_dettaglio.etichetta);
      aggiungi([...base, 'campo_dettaglio', 'segnaposto'], g, 'Campo che compare · esempio', d.campo_dettaglio.segnaposto);
    }
    (d.scorciatoie ?? []).forEach((s) => aggiungi([...base, 'scorciatoie', String(s.valore)], g, `Tasto rapido ${s.valore}`, s.etichetta));
  });

  if (q.contatti) {
    const g = 'Ultima schermata';
    aggiungi(['contatti', 'domanda'], g, 'Domanda', q.contatti.domanda);
    q.contatti.campi.forEach((c) => {
      aggiungi(['contatti', 'campi', c.id, 'etichetta'], g, `Campo ${c.id} · etichetta`, c.etichetta);
      aggiungi(['contatti', 'campi', c.id, 'segnaposto'], g, `Campo ${c.id} · esempio`, c.segnaposto);
      aggiungi(['contatti', 'campi', c.id, 'nota'], g, `Campo ${c.id} · nota`, c.nota);
    });
    aggiungi(['contatti', 'indicatore'], g, 'Indicatore in alto', q.contatti.indicatore);
    aggiungi(['contatti', 'aiuto'], g, 'Riga di aiuto', q.contatti.aiuto);
    aggiungi(['contatti', 'bottone'], g, 'Bottone di invio', q.contatti.bottone);
    if (q.contatti.consenso) {
      aggiungi(['contatti', 'consenso', 'testo'], g, 'Consenso · testo', q.contatti.consenso.testo, { lungo: true });
      aggiungi(['contatti', 'consenso', 'etichetta_informativa'], g, 'Consenso · collegamento all\'informativa', q.contatti.consenso.etichetta_informativa);
      aggiungi(['contatti', 'consenso', 'url_informativa'], g, 'Consenso · indirizzo dell\'informativa', q.contatti.consenso.url_informativa, { riferimento: true });
    }
  }

  if (q.conferma) {
    const g = 'Conferma';
    aggiungi(['conferma', 'titolo'], g, 'Titolo', q.conferma.titolo);
    aggiungi(['conferma', 'in_grande'], g, 'Scritta grande', q.conferma.in_grande);
    aggiungi(['conferma', 'testo'], g, 'Testo', q.conferma.testo, { lungo: true });
    aggiungi(['conferma', 'testo_se_consenso'], g, 'Testo per chi ha chiesto gli aggiornamenti', q.conferma.testo_se_consenso, { lungo: true });
    if (q.conferma.collegamento) {
      aggiungi(['conferma', 'collegamento', 'prima'], g, 'Collegamento · frase prima', q.conferma.collegamento.prima);
      aggiungi(['conferma', 'collegamento', 'etichetta'], g, 'Collegamento · testo', q.conferma.collegamento.etichetta);
      aggiungi(['conferma', 'collegamento', 'url'], g, 'Collegamento · indirizzo', q.conferma.collegamento.url, { riferimento: true });
    }
  }

  if (q.email_conferma) {
    const g = 'Email di conferma';
    const e = q.email_conferma;
    aggiungi(['email_conferma', 'oggetto'], g, 'Oggetto', e.oggetto);
    aggiungi(['email_conferma', 'titolo'], g, 'Titolo', e.titolo);
    aggiungi(['email_conferma', 'testo'], g, 'Testo', e.testo, { lungo: true });
    aggiungi(['email_conferma', 'perche'], g, 'Perché la si riceve', e.perche, { lungo: true });
    aggiungi(['email_conferma', 'anteprima'], g, 'Anteprima', e.anteprima);
    aggiungi(['email_conferma', 'in_grande'], g, 'Scritta grande', e.in_grande);
    aggiungi(['email_conferma', 'titolo_riepilogo'], g, 'Titolo del riepilogo', e.titolo_riepilogo);
    aggiungi(['email_conferma', 'testo_consenso'], g, 'Testo sopra il bottone di conferma', e.testo_consenso, { lungo: true });
    aggiungi(['email_conferma', 'bottone_consenso'], g, 'Bottone di conferma', e.bottone_consenso);
    aggiungi(['email_conferma', 'etichetta_uscita'], g, 'Collegamento per uscire', e.etichetta_uscita);
  }
  return voci;
}

/** Il testo tradotto di una voce ('' se manca). */
export function leggiVoce(t: TraduzioneQuestionario | undefined, segmenti: string[]): string {
  let nodo: unknown = t;
  for (const s of segmenti) {
    if (!nodo || typeof nodo !== 'object') return '';
    nodo = (nodo as Record<string, unknown>)[s];
  }
  return typeof nodo === 'string' ? nodo : '';
}

/**
 * La traduzione con una voce scritta. Una stringa vuota si scrive vuota: la pulizia prima del
 * salvataggio (`traduzioniPulite`) la toglie, perché una voce vuota vale come assente.
 */
export function scriviVoce(t: TraduzioneQuestionario | undefined, segmenti: string[], valore: string): TraduzioneQuestionario {
  const radice: Record<string, unknown> = JSON.parse(JSON.stringify(t ?? {}));
  let nodo = radice;
  segmenti.forEach((s, i) => {
    if (i === segmenti.length - 1) {
      nodo[s] = valore;
      return;
    }
    const figlio = nodo[s];
    if (!figlio || typeof figlio !== 'object' || Array.isArray(figlio)) nodo[s] = {};
    nodo = nodo[s] as Record<string, unknown>;
  });
  return radice as TraduzioneQuestionario;
}
